import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { delay } from '~/common/utils/async.util';
import { getProductsApi } from '~/features/products/api/get-products.api';

import type { RootState } from '~/common/stores';
import type { ProductItem } from '~/features/products/types/products.type';

export interface ProductsState {
  isLoading: boolean;
  items: ProductItem[];
}

const initialState: ProductsState = {
  // 페이지 마운트 시 항상 fetchProducts 를 호출하므로 초기값 true — fetch 시작 전 한 프레임에
  // EmptyState('상품 준비 중')가 번쩍이는 깜박임 방지(스켈레톤이 먼저). 소비처는 useProducts 1곳.
  isLoading: true,
  items: [],
};

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      const [{ data }] = await Promise.all([getProductsApi(), delay(200)]);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const ProductsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchProducts.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.items = action.payload.map(item => ({
          ...item,
          amount: Number(item.amount),
          vat: Number(item.vat),
          totalToken: Number(item.totalToken),
          pricePerToken: Number(item.pricePerToken),
          tokenUsagePerMinute: Number(item.tokenUsagePerMinute),
          expirationDays: Number(item.expirationDays),
        }));
        state.isLoading = false;
      })
      .addCase(fetchProducts.rejected, state => {
        state.items = [];
        state.isLoading = false;
      });
  },
});

export const selectProducts = (state: RootState) => state.products.items;

export default ProductsSlice;
